import React from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Rating,
  Paper,
  Stack,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { UISitterDisplay } from "types/ui.sitter.types";

interface SitterCardProps {
  sitter: UISitterDisplay;
  isSelected?: boolean;
  onCardClick?: () => void;
  onViewProfile?: () => void;
  onBookNow?: () => void;
}

const SitterCard: React.FC<SitterCardProps> = ({
  sitter,
  isSelected = false,
  onCardClick,
  onViewProfile,
  onBookNow,
}) => {
  return (
    <Paper
      elevation={isSelected ? 4 : 1}
      onClick={onCardClick}
      sx={{
        p: 2.5,
        mb: 2,
        cursor: "pointer",
        border: isSelected ? "1px solid #178a8e85" : "1px solid transparent",
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 3,
        },
      }}
    >
      {/* Header with Avatar, Name, and Price */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          mb: 1.5,
          width: "100%",
        }}
      >
        <Avatar
          src={sitter.photoUrl}
          alt={sitter.name}
          sx={{
            width: 56,
            height: 56,
            mr: 2,
            backgroundColor: "#178B8E",
          }}
        >
          {sitter.name.charAt(0)}
        </Avatar>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{sitter.name}</Typography>
            <Typography variant="h6">${sitter.price} / night</Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={0.5}
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              <StarIcon sx={{ fontSize: 16, color: "#FDB833" }} />
              <Typography variant="body2">
                {sitter.rating.toFixed(1)}
              </Typography>
            </Stack>
            <Typography variant="body2">
              {sitter.reviewCount} reviews
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: "#5A5A5A" }}>
            {sitter.location}
          </Typography>
        </Box>
      </Box>

      {/* Review Snippet */}
      <Typography
        variant="body2"
        sx={{
          color: "#5A5A5A",
          mb: 2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        "{sitter.reviewSnippet}"
      </Typography>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            onViewProfile?.();
          }}
          sx={{
            lineHeight: 1.3,
            fontSize: 14,
            height: "50px",
            flex: 1,
            textTransform: "none",
            fontWeight: 550,
            color: "#178B8E",
            "&:hover": {
              backgroundColor: "rgba(23, 139, 142, 0.08)",
            },
          }}
        >
          View Full Profile
        </Button>
        <Button
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onBookNow?.();
          }}
          sx={{
            padding: "12px 24px",
            whiteSpace: "nowrap",
            fontSize: 14,
            height: "50px",
            flex: 1,
            textTransform: "none",
            fontWeight: 550,
            backgroundColor: "#178B8E",
            "&:hover": {
              backgroundColor: "#0F6164",
            },
          }}
        >
          Book now!
        </Button>
      </Box>
    </Paper>
  );
};

export default SitterCard;
